import db from './db';
import { mapObjToArray } from '../utils/utils';
import { mapWarrior } from '../utils/warriorUtils';
import { getTopClans, getArenaStandings } from './dbCalls';
import { getSeasonId } from '../utils/arenaUtils';
import { CLAN } from './config';
import { InitDataType, WarriorType, ClanType } from './types';

export const TTL_MS = 60 * 60 * 1000;

export type Leader = {
  rank: number;
  id: string;
  login: string;
  level: number;
  experience: number;
  klass: 'strength' | 'dexterity' | 'intuition';
  wins: number;
  losses: number;
};

let cache: Leader[] | null = null;
let fetchedAt = 0;

export function __resetLeaderboardCacheForTests() {
  cache = null;
  fetchedAt = 0;
}

export async function fetchLeaders(initData: InitDataType): Promise<Leader[]> {
  if (cache && Date.now() - fetchedAt < TTL_MS) return cache;

  const snap = await db().child('warriors').once('value');
  const raw = mapObjToArray(snap.val() || {}) as any[];

  // Filter bots before mapping: mapWarrior invokes updateFeature which
  // assumes a feature field that raw bot records in Firebase may lack.
  const warriors: WarriorType[] = raw.filter((w) => !w.isBot).map((w) => mapWarrior(w, initData));

  warriors.sort((a, b) => b.level - a.level || b.experience - a.experience);

  cache = warriors.slice(0, 20).map((w, i) => {
    const { strength = 0, dexterity = 0, intuition = 0 } = w.feature;
    const klass: Leader['klass'] =
      strength > intuition && strength > dexterity
        ? 'strength'
        : dexterity > intuition
        ? 'dexterity'
        : 'intuition';
    return {
      rank: i + 1,
      id: w.id,
      login: w.login,
      level: w.level,
      experience: w.experience,
      klass,
      wins: w.numberOfWins ?? 0,
      losses: w.numberOfLosses ?? 0,
    };
  });
  fetchedAt = Date.now();
  return cache;
}

export type ClanLeader = {
  rank: number;
  id: string;
  name: string;
  tag: string;
  members: number;
  warWins: number;
};

let clanCache: ClanLeader[] | null = null;
let clanFetchedAt = 0;

export function __resetClanLeaderboardCacheForTests() {
  clanCache = null;
  clanFetchedAt = 0;
}

export async function fetchClanLeaders(force?: boolean): Promise<ClanLeader[]> {
  if (!force && clanCache && Date.now() - clanFetchedAt < TTL_MS) return clanCache;

  let clans: ClanType[] = [];
  try {
    clans = await getTopClans(CLAN.TOP_CLANS_LIMIT);
  } catch (e) {
    // A ranking read failure shouldn't break the clan screen.
    return [];
  }

  clanCache = clans.map((c, i) => ({
    rank: i + 1,
    id: c.id,
    name: c.name,
    tag: c.tag,
    members: c.members ? Object.keys(c.members).length : 0,
    warWins: c.warWins || 0,
  }));
  clanFetchedAt = Date.now();
  return clanCache;
}

export type ArenaLeader = {
  rank: number;
  id: string;
  login: string;
  rating: number;
  wins: number;
  losses: number;
};

/**
 * Current-season ranked standings, sorted by rating. Always read fresh (no
 * cache) so a just-played match's rating/W-L shows immediately; the standings
 * node is small, unlike the cached full-warrior XP scan.
 */
export async function fetchArenaLeaders(): Promise<ArenaLeader[]> {
  const season = getSeasonId(Date.now());

  let standings: Awaited<ReturnType<typeof getArenaStandings>> = [];
  try {
    standings = await getArenaStandings(season);
  } catch (e) {
    return [];
  }

  return standings
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 20)
    .map((s, i) => ({
      rank: i + 1,
      id: s.id,
      login: s.login,
      rating: s.rating,
      wins: s.wins,
      losses: s.losses,
    }));
}
